import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { colors } from '@guryou/colors';
import { authCookie, cognitoLogout, handleLoginFromCookie, logoutCookie } from '@guryou/redux';
import { clearWidgetAuthIntegrationContext } from 'constants/widgetIntegrationBranding';
import LogoLight from 'assets/img/guryouFeelLogos/gyFeelLogo13.png';
import Logo from 'assets/img/logo.png';
import ForgotPassword from 'containers/authComponents/ForgotPassword';
import Login from 'containers/authComponents/Login';
import SignUp from 'containers/authComponents/SignUp';
import CustomModal from '../CustomModal';
import { Icon } from '../shared';
import useUnreadMessages from '../../hooks/useUnreadMessages';

const menuItems = [
  { label: 'BOOK', route: `/book`, requiresLogin: false },
  { label: 'VIDEO', route: `/videos`, requiresLogin: false },
  { label: 'MY VIDEOS', route: `/my-video-courses`, requiresLogin: true },
];

const SubHeader = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const auth = useSelector(s => s.auth);
  const hasAuthData = !auth.data;
  const { hasUnreadMessages } = useUnreadMessages();

  const [view, setView] = useState();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isHome = history.location.pathname === '/';
  const isPublicHeroPage = ['/book', '/videos'].includes(history.location.pathname);
  // When user is logged in on home, use the book page style (white with shadow)
  const isHomeWithAuth = isHome && auth.data;
  const useTransparentNav = (isHome && !isHomeWithAuth) || (isPublicHeroPage && !auth.data);
  // Use LogoLight on public hero pages when user is not logged in
  const currentLogo = useTransparentNav ? LogoLight : Logo;

  useEffect(() => {
    if (authCookie() && hasAuthData) {
      dispatch(handleLoginFromCookie(authCookie()));
    }
    if (!hasAuthData) {
      setIsModalOpen(false);
    }
  }, [dispatch, hasAuthData]);

  // Control mobile menu and dropdown with useEffect only
  useEffect(() => {
    const handleClickOutside = event => {
      // Close dropdown when clicking outside the dropdown container
      if (showDropdown && !event.target.closest('[data-user-dropdown]')) {
        setShowDropdown(false);
      }

      // Close mobile menu when clicking outside
      if (isMobileMenuOpen && !event.target.closest('#mobile-menu-2') && !event.target.closest('[data-mobile-menu-button]')) {
        setIsMobileMenuOpen(false);
      }
    };

    // Handle mobile menu button clicks and navigation
    const handleMobileMenuToggle = event => {
      if (event.target.closest('[data-mobile-menu-button]')) {
        setIsMobileMenuOpen(prev => !prev);
      }

      // Close mobile menu when clicking on navigation links or auth buttons
      if (event.target.closest('[data-mobile-nav-link]') || event.target.closest('[data-mobile-auth-button]')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleMobileMenuToggle);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleMobileMenuToggle);
    };
  }, [showDropdown, isMobileMenuOpen]);

  const handleLogInOrLogOut = viewType => {
    if (auth.data) {
      clearWidgetAuthIntegrationContext();
      dispatch(cognitoLogout());
      logoutCookie();
      return;
    }
    setView(viewType);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setView('login'); // Reset to default view
  };

  const handleLogout = () => {
    clearWidgetAuthIntegrationContext();
    dispatch({ type: 'LOGOUT' });
    dispatch(cognitoLogout());
  };

  return (
    <header className={`absolute z-20 w-full top-16 md:top-20 ${useTransparentNav ? 'bg-transparent' : 'bg-white shadow-sm'}`}>
      <nav className={`${useTransparentNav ? 'bg-transparent' : 'bg-white'} border-gray-200 px-4 lg:px-6 py-3 lg:py-2.5`}>
        <div className="flex flex-wrap justify-between items-center mx-auto max-w-screen-xl">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={currentLogo} className="mr-3 h-6 sm:h-8 lg:h-9" alt="Guryou Logo" />
          </Link>

          {/* Desktop Menu Items */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8 lg:flex-1 lg:justify-center">
            <ul className="flex space-x-8">
              {menuItems.map((item, index) => {
                const showItem = item.requiresLogin ? (auth.data ? true : false) : true;
                return (
                  showItem && (
                    <li key={index}>
                      <Link
                        to={item.route}
                        className={`block py-2 text-sm font-medium capitalize transition-colors ${
                          useTransparentNav
                            ? `text-white hover:text-[var(--feel-primary)]`
                            : `text-[${colors.mineShaft}] hover:text-[var(--feel-primary)]`
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  )
                );
              })}
            </ul>
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-2">
            {/* Chat Button - Only show when user is logged in */}
            {auth.data && (
              <button
                onClick={() => history.push('/chat')}
                className={`relative p-2 rounded-lg transition-colors ${useTransparentNav ? `text-white` : `text-[${colors.mineShaft}]`}`}
                title="Chat"
              >
                <Icon name="chat" size="lg" />
                {/* Unread messages indicator */}
                {hasUnreadMessages && (
                  <span className="absolute top-1 right-1 bg-red-500 rounded-full h-3 w-3 border-2 border-white shadow-lg animate-pulse"></span>
                )}
              </button>
            )}

            {auth.data ? (
              <div className="relative" data-user-dropdown>
                <button
                  type="button"
                  className={`flex text-sm rounded-full focus:ring-4 focus:ring-gray-300 transition-all duration-200 bg-[${colors.mineShaft}]`}
                  id="user-menu-button"
                  aria-expanded="false"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <span className="sr-only">Open user menu</span>
                  <img
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-full"
                    src={`https://ui-avatars.com/api/?name=${auth.data.username}&background=random`}
                    alt="user avatar"
                  />
                </button>

                {showDropdown && (
                  <div
                    className="z-50 mt-2 w-56 text-base list-none bg-white rounded-lg shadow-lg absolute right-0 top-full border border-gray-100"
                    id="user-dropdown"
                  >
                    <div className="py-3 px-4 border-b border-gray-100">
                      <span className="block text-sm font-medium text-gray-900">{auth.data.username}</span>
                      <span className="block text-sm text-gray-500 truncate">{auth.data.email}</span>
                    </div>
                    <ul className="py-1" aria-labelledby="user-menu-button">
                      <li>
                        <button
                          onClick={() => {
                            history.push('/profile');
                            setShowDropdown(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          {t('PROFILE.title')}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleLogout();
                            setShowDropdown(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          {t('PROFILE.logout')}
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex gap-2">
                <button
                  className={`px-3 py-2 text-sm font-medium rounded-lg border-2 transition-colors text-[var(--feel-primary)] border-[var(--feel-primary)] hover:bg-[var(--feel-primary)] hover:text-white`}
                  onClick={() => handleLogInOrLogOut('login')}
                >
                  {t('GENERAL.login')}
                </button>
                <button
                  className={`px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)]`}
                  onClick={() => handleLogInOrLogOut('register')}
                >
                  {t('GENERAL.register')}
                </button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              type="button"
              className={`inline-flex items-center p-2 ml-2 text-sm rounded-lg lg:hidden focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors ${
                useTransparentNav ? 'text-white' : `text-[${colors.mineShaft}]`
              }`}
              data-mobile-menu-button
              aria-controls="mobile-menu-2"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden" id="mobile-menu-2">
            <div className="bg-white px-2 pt-4 pb-3 space-y-1 sm:px-3 border-t border-gray-200 mt-2 rounded-lg shadow-lg mx-2">
              {/* Mobile Menu Items */}
              <div className="space-y-1">
                {menuItems.map((item, index) => {
                  const showItem = item.requiresLogin ? (auth.data ? true : false) : true;
                  return (
                    showItem && (
                      <Link
                        key={index}
                        to={item.route}
                        className={`block px-3 py-2 text-base font-medium capitalize transition-colors rounded-lg hover:bg-gray-50 text-[${colors.mineShaft}]`}
                        data-mobile-nav-link
                      >
                        {item.label}
                      </Link>
                    )
                  );
                })}
              </div>

              {/* Mobile Auth Buttons */}
              {!auth.data && (
                <div className="pt-4 pb-3 border-t border-gray-200 space-y-2">
                  <button
                    className={`block w-full text-left px-3 py-2 text-base font-medium rounded-lg transition-colors hover:bg-gray-50 text-[${colors.mineShaft}] border border-[${colors.mercury}]`}
                    data-mobile-auth-button
                    onClick={() => handleLogInOrLogOut('login')}
                  >
                    {t('GENERAL.login')}
                  </button>
                  <button
                    className={`block w-full text-left px-3 py-2 text-base font-medium text-white rounded-lg transition-colors hover:opacity-90 bg-[var(--feel-primary)]`}
                    data-mobile-auth-button
                    onClick={() => handleLogInOrLogOut('register')}
                  >
                    {t('GENERAL.register')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <CustomModal isOpen={isModalOpen} onClose={handleCloseModal} title={view ? t(`GENERAL.${view}`) : t('GENERAL.login')}>
        {view === 'login' ? (
          <Login setView={setView} />
        ) : view === 'register' ? (
          <SignUp setView={setView} handleCloseModal={handleCloseModal} />
        ) : (
          <ForgotPassword setView={setView} />
        )}
      </CustomModal>
    </header>
  );
};

export default SubHeader;
